import React, { useEffect, useState } from "react";
import { IResourceComponentsProps } from "@refinedev/core";
import { useTable } from "@refinedev/react-table";
import { Column, ColumnDef, flexRender } from "@tanstack/react-table";
import {
  ScrollArea,
  Table,
  Pagination,
  Flex,
  Select,
  LoadingOverlay,
  Text,
  Box,
  TextInput,
  TextInputProps,
  Paper,
  Group,
  ActionIcon,
  Collapse,
  Badge,
  UnstyledButton,
  Grid,
  ThemeIcon,
  Tooltip,
  Skeleton,
  Menu,
  Image,
} from "@mantine/core";
import { List, DateField } from "@refinedev/mantine";
import {
  IconFilter,
  IconFilterOff,
  IconChevronUp,
  IconChevronDown,
  IconX,
  IconEye,
  IconSearch,
  IconStar,
  IconCircleCheck,
  IconClock,
  IconDots,
  IconCopy,
  IconExternalLink,
  IconPhoto,
  IconEyeOff,
} from "@tabler/icons";
import { useNavigate } from "react-router-dom";
import {
  Review,
  RATING_OPTIONS,
  VERIFIED_OPTIONS,
  SOURCE_COLORS,
  SOURCE_LABELS,
} from "entities/review";
import { DateFilter } from "components/common/filters/DateFilter";
import { countryCodeToFlag } from "utils/countryFlag";
import { StarRating, STAR_HEX } from "./StarRating";
import { usePublishReview } from "./usePublishReview";

/* -- Debounced filter input ------------------------------------------------ */
function DebouncedColumnInput({
  column,
  delay = 500,
  ...inputProps
}: {
  column: Column<Review, unknown>;
  delay?: number;
} & Omit<TextInputProps, "value" | "onChange">) {
  const columnValue = (column.getFilterValue() as string) ?? "";
  const [localValue, setLocalValue] = useState(columnValue);

  useEffect(() => {
    setLocalValue(columnValue);
  }, [columnValue]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      column.setFilterValue(localValue || undefined);
    }, delay);
    return () => clearTimeout(timeout);
  }, [localValue, delay, column]);

  return (
    <TextInput
      value={localValue}
      onChange={(e) => setLocalValue(e.currentTarget.value)}
      icon={<IconSearch size={14} />}
      {...inputProps}
    />
  );
}

/* -- Source badge ----------------------------------------------------------- */
function SourceBadge({ source }: { source: string }) {
  return (
    <Badge
      variant="light"
      color={SOURCE_COLORS[source] || "gray"}
      size="sm"
      radius="xl"
      sx={{ fontWeight: 600 }}
    >
      {SOURCE_LABELS[source] || source}
    </Badge>
  );
}

/* -- Status badge ---------------------------------------------------------- */
function StatusBadge({ publishedAt }: { publishedAt: string | null }) {
  const isPublished = !!publishedAt;
  return (
    <Badge
      variant="light"
      color={isPublished ? "green" : "gray"}
      size="sm"
      radius="xl"
      sx={{ fontWeight: 600 }}
    >
      {isPublished ? "Published" : "Unpublished"}
    </Badge>
  );
}

/* -- Row action menu ------------------------------------------------------- */
function RowActions({ record }: { record: Review }) {
  const navigate = useNavigate();
  const { publish, unpublish, isLoading } = usePublishReview();
  const isPublished = !!record.published_at;

  return (
    <Menu
      shadow="lg"
      width={200}
      position="bottom-end"
      withinPortal
      radius="md"
    >
      <Menu.Target>
        <ActionIcon variant="light" color="gray" size="sm" radius="xl">
          <IconDots size={14} />
        </ActionIcon>
      </Menu.Target>
      <Menu.Dropdown>
        <Menu.Item
          icon={<IconEye size={14} />}
          onClick={() => navigate(`/reviews/show/${record.id}`)}
        >
          View details
        </Menu.Item>
        {isPublished ? (
          <Menu.Item
            icon={<IconEyeOff size={14} />}
            color="orange"
            disabled={isLoading}
            onClick={() => unpublish(record.id)}
          >
            Unpublish
          </Menu.Item>
        ) : (
          <Menu.Item
            icon={<IconEye size={14} />}
            color="green"
            disabled={isLoading}
            onClick={() => publish(record.id)}
          >
            Publish
          </Menu.Item>
        )}
        <Menu.Item
          icon={<IconCopy size={14} />}
          onClick={() => {
            const text = [record.title, record.body]
              .filter(Boolean)
              .join("\n\n");
            navigator.clipboard.writeText(text || `Review #${record.id}`);
          }}
        >
          Copy review text
        </Menu.Item>
        {record.review_request && (
          <Menu.Item
            icon={<IconExternalLink size={14} />}
            onClick={() =>
              navigate(`/review_requests/show/${record.review_request!.id}`)
            }
          >
            View review request
          </Menu.Item>
        )}
      </Menu.Dropdown>
    </Menu>
  );
}

/* -- Stat card ------------------------------------------------------------- */
function StatCard({
  icon,
  label,
  value,
  color,
  isLoading,
  subtitle,
}: {
  icon: React.ReactNode;
  label: string;
  value: number | string;
  color: string;
  isLoading: boolean;
  subtitle?: string;
}) {
  return (
    <Paper
      p="sm"
      radius="md"
      sx={(theme) => ({
        backgroundColor:
          theme.colorScheme === "dark" ? theme.colors.dark[6] : theme.white,
        border: `1px solid ${
          theme.colorScheme === "dark"
            ? theme.colors.dark[4]
            : theme.colors.gray[2]
        }`,
        flex: "1 1 0",
        minWidth: 140,
      })}
    >
      <Group spacing="sm" noWrap>
        <ThemeIcon size={36} radius="md" variant="light" color={color}>
          {icon}
        </ThemeIcon>
        <Box>
          {isLoading ? (
            <Skeleton height={20} width={30} radius="sm" />
          ) : (
            <Text size="lg" weight={700} sx={{ lineHeight: 1.1 }}>
              {value}
            </Text>
          )}
          <Text size="xs" color="dimmed" weight={500} mt={2}>
            {label}
          </Text>
          {subtitle && (
            <Text
              size={10}
              color="dimmed"
              weight={400}
              mt={1}
              sx={{ opacity: 0.7 }}
            >
              {subtitle}
            </Text>
          )}
        </Box>
      </Group>
    </Paper>
  );
}

/* -- Main list component --------------------------------------------------- */
export const ReviewList: React.FC<IResourceComponentsProps> = () => {
  const [filtersOpen, setFiltersOpen] = useState(false);
  const navigate = useNavigate();

  const columns = React.useMemo<ColumnDef<Review>[]>(
    () => [
      {
        id: "id",
        accessorKey: "id",
        header: "ID",
        size: 70,
        enableColumnFilter: false,
        cell: ({ getValue }) => (
          <Text
            size="xs"
            color="dimmed"
            weight={600}
            sx={{ fontFamily: "monospace" }}
          >
            #{getValue<number>()}
          </Text>
        ),
      },
      {
        id: "reviewer_name",
        accessorKey: "reviewer_name",
        header: "Reviewer",
        cell: function render({ getValue, row }) {
          const email = row.original.email;
          return (
            <Box>
              <Text size="sm" weight={600} sx={{ lineHeight: 1.3 }}>
                {getValue<string>() || "Anonymous"}
              </Text>
              {email && (
                <Text size="xs" color="dimmed" weight={500}>
                  {email}
                </Text>
              )}
            </Box>
          );
        },
        meta: { filterOperator: "contains" },
      },
      {
        id: "product_name",
        accessorKey: "product_name",
        header: "Product",
        cell: function render({ row }) {
          const title = row.original.product_name;
          const imageUrl = row.original.product_image_url;
          if (!title && !imageUrl) {
            return (
              <Text size="xs" color="dimmed">
                —
              </Text>
            );
          }
          return (
            <Group spacing={8} noWrap sx={{ maxWidth: 240 }}>
              {imageUrl && (
                <Image
                  src={imageUrl}
                  width={28}
                  height={28}
                  radius="sm"
                  fit="cover"
                  withPlaceholder
                />
              )}
              <Text size="xs" weight={500} lineClamp={2}>
                {title || "Untitled"}
              </Text>
            </Group>
          );
        },
        meta: { filterOperator: "contains" },
      },
      {
        id: "rating",
        accessorKey: "rating",
        header: "Rating",
        size: 130,
        cell: ({ getValue }) => (
          <StarRating rating={getValue<number>()} size={14} />
        ),
      },
      {
        id: "title",
        accessorKey: "title",
        header: "Title",
        enableColumnFilter: false,
        cell: function render({ getValue, row }) {
          const title = getValue<string>();
          const body = row.original.body;
          if (!title && !body)
            return (
              <Text size="xs" color="dimmed">
                —
              </Text>
            );
          return (
            <Box sx={{ maxWidth: 220 }}>
              {title && (
                <Text size="xs" weight={600} lineClamp={1}>
                  {title}
                </Text>
              )}
              {body && (
                <Text size={11} color="dimmed" lineClamp={1} mt={title ? 2 : 0}>
                  {body}
                </Text>
              )}
            </Box>
          );
        },
      },
      {
        id: "media",
        header: "Media",
        size: 60,
        enableColumnFilter: false,
        enableSorting: false,
        cell: function render({ row }) {
          const media = row.original.review_media;
          if (!media || media.length === 0) return null;
          return (
            <Tooltip
              label={`${media.length} media file${media.length > 1 ? "s" : ""}`}
              withinPortal
            >
              <Group spacing={4} noWrap>
                <IconPhoto size={14} color="#868e96" />
                <Text size="xs" weight={600} color="dimmed">
                  {media.length}
                </Text>
              </Group>
            </Tooltip>
          );
        },
        meta: { isPlaceholder: true },
      },
      {
        id: "source",
        accessorKey: "source",
        header: "Source",
        enableColumnFilter: false,
        cell: ({ getValue }) => <SourceBadge source={getValue<string>()} />,
      },
      {
        id: "country_code",
        accessorKey: "country_code",
        header: "Country",
        size: 80,
        cell: function render({ getValue }) {
          const value = getValue<string>();
          if (!value)
            return (
              <Text size="xs" color="dimmed">
                —
              </Text>
            );
          return (
            <Tooltip label={value} withinPortal>
              <Group spacing={6} noWrap>
                <Text size="md" sx={{ lineHeight: 1 }}>
                  {countryCodeToFlag(value)}
                </Text>
                <Text size="xs" weight={600} color="dimmed">
                  {value}
                </Text>
              </Group>
            </Tooltip>
          );
        },
        meta: { filterOperator: "contains" },
      },
      {
        id: "is_verified",
        accessorKey: "is_verified",
        header: "Verified",
        size: 90,
        cell: ({ getValue }) => {
          const verified = getValue<boolean>();
          return verified ? (
            <ThemeIcon size={20} radius="xl" color="green" variant="light">
              <IconCircleCheck size={14} />
            </ThemeIcon>
          ) : (
            <ThemeIcon size={20} radius="xl" color="gray" variant="light">
              <IconClock size={14} />
            </ThemeIcon>
          );
        },
      },
      {
        id: "published_at",
        accessorKey: "published_at",
        header: "Status",
        size: 110,
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ getValue }) => (
          <StatusBadge publishedAt={getValue<string | null>()} />
        ),
      },
      {
        id: "created_at",
        accessorKey: "created_at",
        header: "Created",
        cell: function render({ getValue }) {
          return (
            <Text size="xs" color="dimmed">
              <DateField value={getValue<any>()} format="MMM D, YYYY" />
            </Text>
          );
        },
        meta: { filterOperator: "between" },
      },
      {
        id: "updated_at",
        accessorKey: "updated_at",
        header: "Updated",
        cell: function render({ getValue }) {
          return (
            <Text size="xs" color="dimmed">
              <DateField value={getValue<any>()} format="MMM D, YYYY" />
            </Text>
          );
        },
        meta: { filterOperator: "between" },
      },
      {
        id: "actions",
        header: "",
        size: 44,
        cell: function render({ row: { original: record } }) {
          return <RowActions record={record} />;
        },
        meta: { isPlaceholder: true },
      },
    ],
    [navigate],
  );

  const {
    getHeaderGroups,
    getRowModel,
    setOptions,
    getFlatHeaders,
    refineCore: {
      setCurrent,
      pageCount,
      current,
      tableQueryResult: { data: tableData, isFetching },
    },
  } = useTable({
    columns,
    refineCoreProps: {
      sorters: {
        initial: [
          {
            field: "id",
            order: "desc",
          },
        ],
      },
      pagination: {
        pageSize: 10,
      },
    },
  });

  setOptions((prev) => ({
    ...prev,
    meta: { ...prev.meta },
  }));

  const headers = getFlatHeaders();
  const reviewerNameColumn = headers.find(
    (h) => h.id === "reviewer_name",
  )?.column;
  const productNameColumn = headers.find(
    (h) => h.id === "product_name",
  )?.column;
  const ratingColumn = headers.find((h) => h.id === "rating")?.column;
  const countryCodeColumn = headers.find(
    (h) => h.id === "country_code",
  )?.column;
  const isVerifiedColumn = headers.find((h) => h.id === "is_verified")?.column;
  const createdAtHeader = headers.find((h) => h.id === "created_at");
  const updatedAtHeader = headers.find((h) => h.id === "updated_at");

  const ratingValue = (ratingColumn?.getFilterValue() as string) ?? null;
  const isVerifiedValue =
    (isVerifiedColumn?.getFilterValue() as string) ?? null;

  const appliedFilters = headers.filter((h) => h.column.getIsFiltered());
  const hasActiveFilters = appliedFilters.length > 0;

  const clearAllFilters = () => {
    appliedFilters.forEach((h) => h.column.setFilterValue(undefined));
  };

  const allRecords = tableData?.data ?? [];
  const totalCount = tableData?.total ?? 0;
  const avgRating =
    allRecords.length > 0
      ? (
          allRecords.reduce((sum, r) => sum + (r.rating || 0), 0) /
          allRecords.length
        ).toFixed(1)
      : "0.0";
  const verifiedCount = allRecords.filter((r) => r.is_verified).length;
  const unverifiedCount = allRecords.filter((r) => !r.is_verified).length;

  return (
    <List canCreate={false}>
      {/* -- Summary stats -------------------------------------------------- */}
      <Flex gap="sm" mb="lg" wrap="wrap">
        <StatCard
          icon={<IconStar size={18} />}
          label="Total Reviews"
          value={totalCount}
          color="blue"
          isLoading={isFetching}
        />
        <StatCard
          icon={<IconStar size={18} />}
          label="Avg Rating"
          value={avgRating}
          color="yellow"
          isLoading={isFetching}
          subtitle="This page"
        />
        <StatCard
          icon={<IconCircleCheck size={18} />}
          label="Verified"
          value={verifiedCount}
          color="green"
          isLoading={isFetching}
          subtitle="This page"
        />
        <StatCard
          icon={<IconClock size={18} />}
          label="Unverified"
          value={unverifiedCount}
          color="gray"
          isLoading={isFetching}
          subtitle="This page"
        />
      </Flex>

      {/* -- Filter bar ----------------------------------------------------- */}
      <Paper
        p="md"
        mb="md"
        radius="md"
        sx={(theme) => ({
          backgroundColor:
            theme.colorScheme === "dark"
              ? theme.colors.dark[7]
              : theme.colors.gray[0],
          border: `1px solid ${
            theme.colorScheme === "dark"
              ? theme.colors.dark[5]
              : theme.colors.gray[2]
          }`,
        })}
      >
        <Group position="apart" mb={filtersOpen ? "md" : 0}>
          <UnstyledButton onClick={() => setFiltersOpen((o) => !o)}>
            <Group spacing={8}>
              <ThemeIcon
                size={28}
                radius="md"
                variant="light"
                color={hasActiveFilters ? "blue" : "gray"}
              >
                <IconFilter size={14} />
              </ThemeIcon>
              <Text size="sm" weight={600}>
                Filters
              </Text>
              {hasActiveFilters && (
                <Badge
                  size="xs"
                  variant="filled"
                  color="blue"
                  radius="xl"
                  sx={{ minWidth: 20 }}
                >
                  {appliedFilters.length}
                </Badge>
              )}
              {filtersOpen ? (
                <IconChevronUp size={14} />
              ) : (
                <IconChevronDown size={14} />
              )}
            </Group>
          </UnstyledButton>

          {hasActiveFilters && (
            <Tooltip label="Clear all filters" withinPortal>
              <ActionIcon
                variant="light"
                color="gray"
                size="sm"
                radius="xl"
                onClick={clearAllFilters}
              >
                <IconFilterOff size={14} />
              </ActionIcon>
            </Tooltip>
          )}
        </Group>

        <Collapse in={filtersOpen}>
          <Grid gutter="md">
            {reviewerNameColumn && (
              <Grid.Col span={4}>
                <DebouncedColumnInput
                  column={reviewerNameColumn}
                  label="Reviewer Name"
                  placeholder="Search by name..."
                  size="xs"
                />
              </Grid.Col>
            )}
            {productNameColumn && (
              <Grid.Col span={4}>
                <DebouncedColumnInput
                  column={productNameColumn}
                  label="Product"
                  placeholder="Search by product..."
                  size="xs"
                />
              </Grid.Col>
            )}
            <Grid.Col span={4}>
              <Select
                label="Rating"
                placeholder="All ratings"
                data={RATING_OPTIONS}
                value={ratingValue}
                onChange={(value) =>
                  ratingColumn?.setFilterValue(value || undefined)
                }
                clearable
                size="xs"
              />
            </Grid.Col>
            {countryCodeColumn && (
              <Grid.Col span={4}>
                <DebouncedColumnInput
                  column={countryCodeColumn}
                  label="Country Code"
                  placeholder="Search country..."
                  size="xs"
                />
              </Grid.Col>
            )}
            <Grid.Col span={4}>
              <Select
                label="Verified"
                placeholder="All"
                data={VERIFIED_OPTIONS}
                value={isVerifiedValue}
                onChange={(value) =>
                  isVerifiedColumn?.setFilterValue(value || undefined)
                }
                clearable
                size="xs"
              />
            </Grid.Col>
            {createdAtHeader && (
              <Grid.Col span={6}>
                <Text size="xs" weight={500} mb={4}>
                  Created At
                </Text>
                <DateFilter
                  header={createdAtHeader}
                  defaultMode="single"
                  enableModeToggle={false}
                  size="xs"
                />
              </Grid.Col>
            )}
            {updatedAtHeader && (
              <Grid.Col span={6}>
                <Text size="xs" weight={500} mb={4}>
                  Updated At
                </Text>
                <DateFilter
                  header={updatedAtHeader}
                  defaultMode="single"
                  enableModeToggle={false}
                  size="xs"
                />
              </Grid.Col>
            )}
          </Grid>
        </Collapse>

        {/* Applied filter chips */}
        {hasActiveFilters && !filtersOpen && (
          <Flex gap={6} wrap="wrap" mt="xs">
            {appliedFilters.map((header) => {
              const filterValue = header.column.getFilterValue();
              let displayValue: string;

              if (header.id === "rating") {
                displayValue = `${filterValue} Star${Number(filterValue) !== 1 ? "s" : ""}`;
              } else if (header.id === "is_verified") {
                displayValue =
                  filterValue === "true" ? "Verified" : "Unverified";
              } else if (
                header.id === "created_at" ||
                header.id === "updated_at"
              ) {
                const arr = filterValue as string[];
                displayValue = arr
                  ? `${arr[0]?.split("T")[0] ?? ""} — ${arr[1]?.split("T")[0] ?? ""}`
                  : String(filterValue);
              } else {
                displayValue = String(filterValue);
              }

              return (
                <Badge
                  key={header.id}
                  variant="outline"
                  color="blue"
                  size="sm"
                  radius="xl"
                  pr={3}
                  rightSection={
                    <ActionIcon
                      size={14}
                      color="pink"
                      radius="xl"
                      variant="transparent"
                      onClick={() => header.column.setFilterValue(undefined)}
                    >
                      <IconX size={10} />
                    </ActionIcon>
                  }
                >
                  {header.column.columnDef.header as string}: {displayValue}
                </Badge>
              );
            })}
          </Flex>
        )}
      </Paper>

      {/* -- Table ---------------------------------------------------------- */}
      <Paper
        radius="md"
        sx={(theme) => ({
          border: `1px solid ${
            theme.colorScheme === "dark"
              ? theme.colors.dark[4]
              : theme.colors.gray[2]
          }`,
          overflow: "hidden",
        })}
      >
        <ScrollArea>
          <Box pos="relative" sx={{ minWidth: "100%" }}>
            <LoadingOverlay
              visible={isFetching}
              overlayOpacity={0.3}
              radius="md"
            />
            <Table
              highlightOnHover
              verticalSpacing={10}
              horizontalSpacing="md"
              sx={(theme) => ({
                tableLayout: "auto",
                width: "100%",
                "thead tr th": {
                  backgroundColor:
                    theme.colorScheme === "dark"
                      ? theme.colors.dark[7]
                      : theme.colors.gray[0],
                  fontSize: 11,
                  fontWeight: 700,
                  textTransform: "uppercase" as const,
                  letterSpacing: 0.6,
                  color:
                    theme.colorScheme === "dark"
                      ? theme.colors.dark[1]
                      : theme.colors.gray[6],
                  borderBottom: `2px solid ${
                    theme.colorScheme === "dark"
                      ? theme.colors.dark[4]
                      : theme.colors.gray[2]
                  }`,
                  padding: "10px 16px",
                },
                "tbody tr td": {
                  borderBottom: `1px solid ${
                    theme.colorScheme === "dark"
                      ? theme.colors.dark[5]
                      : theme.colors.gray[1]
                  }`,
                  padding: "10px 16px",
                },
                "tbody tr:last-of-type td": {
                  borderBottom: "none",
                },
                "tbody tr:hover": {
                  backgroundColor:
                    theme.colorScheme === "dark"
                      ? theme.colors.dark[6]
                      : theme.fn.lighten(theme.colors.pink[0], 0.5),
                },
              })}
            >
              <thead>
                {getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <th
                        key={header.id}
                        style={{
                          whiteSpace: "nowrap",
                          cursor: header.column.getCanSort()
                            ? "pointer"
                            : "default",
                        }}
                        onClick={header.column.getToggleSortingHandler()}
                      >
                        {!header.isPlaceholder &&
                          flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                        {header.column.getIsSorted() === "asc" && " ↑"}
                        {header.column.getIsSorted() === "desc" && " ↓"}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {getRowModel().rows.map((row) => (
                  <tr key={row.id}>
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
                {getRowModel().rows.length === 0 && !isFetching && (
                  <tr>
                    <td colSpan={columns.length}>
                      <Flex direction="column" align="center" py={48} gap="xs">
                        <ThemeIcon
                          size={48}
                          radius="xl"
                          variant="light"
                          color="gray"
                        >
                          <IconStar size={24} />
                        </ThemeIcon>
                        <Text size="sm" color="dimmed" weight={500}>
                          No reviews found
                        </Text>
                        <Text size="xs" color="dimmed">
                          Try adjusting your filters
                        </Text>
                      </Flex>
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </Box>
        </ScrollArea>
      </Paper>

      {/* -- Pagination ----------------------------------------------------- */}
      <Flex justify="space-between" align="center" mt="md">
        <Text size="xs" color="dimmed">
          Page {current} of {pageCount || 1}
        </Text>
        <Pagination
          total={pageCount}
          page={current}
          onChange={setCurrent}
          radius="md"
          size="sm"
        />
      </Flex>
    </List>
  );
};
